import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, TextInput, Select, SelectItem, ProgressIndicator, ProgressStep, Loading } from '@carbon/react';
import { ArrowRight, ArrowLeft, Delivery, Location } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import AddressAutosuggestInput from '../components/AddressAutosuggestInput';
import { useAuth } from '../context/AuthContext';
import { redirectToStripeCheckout } from '../services/stripeService';

const ShippingPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { cartItems, cartTotal } = useCart();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Load saved shipping data on component mount
  useEffect(() => {
    const savedShipping = localStorage.getItem('chillverify-shipping');
    if (savedShipping) {
      try {
        const parsedData = JSON.parse(savedShipping);
        setFormData(parsedData);
        console.log('✅ Loaded saved shipping data:', parsedData);
      } catch (error) {
        console.error('❌ Failed to load saved shipping data:', error);
      }
    }
  }, []);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const subtotal = cartTotal;
  const shipping = subtotal > 200 ? 0 : 9.99;
  const tax = subtotal * 0.10; // GST 10%
  const total = subtotal + shipping + tax;

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    if (errors[name]) setErrors({ ...errors, [name]: '' });
    
    // Auto-save shipping data as user types
    localStorage.setItem('chillverify-shipping', JSON.stringify(updatedFormData));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName) newErrors.firstName = 'First name is required';
    if (!formData.lastName) newErrors.lastName = 'Last name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.phone) newErrors.phone = 'Phone is required';
    if (!formData.address) newErrors.address = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.zipCode) newErrors.zipCode = 'ZIP code is required';
    if (!formData.country) newErrors.country = 'Country is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Save shipping data
      localStorage.setItem('chillverify-shipping', JSON.stringify(formData));
      
      // Prepare order data for Stripe (matching sensorpushweb format exactly)
      const orderData = {
        items: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image, // Include image so it's stored in the order
        })),
        shippingInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country || 'US',
        },
        total: total, // Total amount including shipping and tax
      };

      // Redirect to Stripe hosted checkout (matching sensorpushweb)
      await redirectToStripeCheckout(orderData, user?.id);
    } catch (error) {
      console.error('❌ Error creating Stripe checkout:', error);
      alert('Failed to proceed to payment. Please try again.');
      setIsLoading(false);
    }
  };

  // Redirect to cart if empty
  if (cartItems.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="shipping-page">
      {/* Progress Header */}
      <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '16px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <ProgressIndicator currentIndex={0} spaceEqually>
                <ProgressStep label="Shipping" />
                <ProgressStep label="Payment" />
                <ProgressStep label="Confirmation" />
              </ProgressIndicator>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Content */}
      <div style={{ padding: '16px 0 32px' }}>
        <Grid>
          {/* Shipping Form */}
          <Column lg={10} md={8} sm={4}>
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{ backgroundColor: '#0f62fe', borderRadius: '8px', padding: '10px' }}>
                  <Delivery size={20} style={{ color: '#ffffff' }} />
                </div>
                <h2 style={{ color: textPrimary, fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Shipping Information</h2>
              </div>

              <form onSubmit={handleSubmit} data-testid="shipping-form">
                {/* Contact Info Section */}
                <p style={{ color: textSecondary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '12px' }}>Contact Information</p>
                
                {/* Name Row - Stack on mobile */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                  <TextInput
                    id="firstName"
                    name="firstName"
                    labelText="First Name"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    invalid={!!errors.firstName}
                    invalidText={errors.firstName}
                    size="lg"
                    data-testid="first-name"
                  />
                  <TextInput
                    id="lastName"
                    name="lastName"
                    labelText="Last Name"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    invalid={!!errors.lastName}
                    invalidText={errors.lastName}
                    size="lg"
                    data-testid="last-name"
                  />
                </div>

                {/* Email & Phone - Stack on mobile */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                  <TextInput
                    id="email"
                    name="email"
                    type="email"
                    labelText="Email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    invalid={!!errors.email}
                    invalidText={errors.email}
                    size="lg"
                    data-testid="email"
                  />
                  <TextInput
                    id="phone"
                    name="phone"
                    type="tel"
                    labelText="Phone"
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={handleChange}
                    invalid={!!errors.phone}
                    invalidText={errors.phone}
                    size="lg"
                    data-testid="phone"
                  />
                </div>

                {/* Address Section */}
                <p style={{ color: textSecondary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '12px' }}>Shipping Address</p>

                <div style={{ marginBottom: '12px' }}>
                  <label style={{ 
                    display: 'block', 
                    color: textPrimary, 
                    fontSize: '0.875rem', 
                    fontWeight: '400', 
                    marginBottom: '8px' 
                  }}>
                    Street Address
                  </label>
                  <AddressAutosuggestInput
                    placeholder="Start typing your address..."
                    value={formData.address}
                    onChangeText={(text) => {
                      const updatedFormData = { ...formData, address: text };
                      setFormData(updatedFormData);
                      // Auto-save shipping data as user types
                      localStorage.setItem('chillverify-shipping', JSON.stringify(updatedFormData));
                    }}
                    onAddressSelected={(addressData) => {
                      const updatedFormData = {
                        ...formData,
                        address: addressData.streetAddress || '',
                        city: addressData.city || '',
                        state: addressData.state || '',
                        zipCode: addressData.zipCode || '',
                        country: addressData.country || 'US'
                      };
                      setFormData(updatedFormData);
                      // Auto-save when address is selected
                      localStorage.setItem('chillverify-shipping', JSON.stringify(updatedFormData));
                    }}
                    style={{ width: '100%' }}
                  />
                  {errors.address && (
                    <div style={{ color: '#da1e28', fontSize: '0.75rem', marginTop: '4px' }}>
                      {errors.address}
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <TextInput
                    id="apartment"
                    name="apartment"
                    labelText="Apt, Suite, etc. (optional)"
                    placeholder="Apt 4B"
                    value={formData.apartment}
                    onChange={handleChange}
                    size="lg"
                    data-testid="apartment"
                  />
                </div>

                {/* City, State, ZIP - Responsive grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ gridColumn: 'span 2' }}>
                    <TextInput
                      id="city"
                      name="city"
                      labelText="City"
                      placeholder="San Francisco"
                      value={formData.city}
                      onChange={handleChange}
                      invalid={!!errors.city}
                      invalidText={errors.city}
                      size="lg"
                      data-testid="city"
                    />
                  </div>
                  <TextInput
                    id="state"
                    name="state"
                    labelText="State/Province"
                    placeholder="CA"
                    value={formData.state}
                    onChange={handleChange}
                    invalid={!!errors.state}
                    invalidText={errors.state}
                    size="lg"
                    data-testid="state"
                  />
                  <TextInput
                    id="zipCode"
                    name="zipCode"
                    labelText="ZIP/Postal Code"
                    placeholder="94102"
                    value={formData.zipCode}
                    onChange={handleChange}
                    invalid={!!errors.zipCode}
                    invalidText={errors.zipCode}
                    size="lg"
                    data-testid="zip-code"
                  />
                </div>

                {/* Country Dropdown */}
                <div style={{ marginBottom: '24px' }}>
                  <Select
                    id="country"
                    name="country"
                    labelText="Country"
                    value={formData.country}
                    onChange={handleChange}
                    invalid={!!errors.country}
                    invalidText={errors.country}
                    size="lg"
                    data-testid="country"
                  >
                    <SelectItem value="AF" text="Afghanistan" />
                    <SelectItem value="AL" text="Albania" />
                    <SelectItem value="DZ" text="Algeria" />
                    <SelectItem value="AD" text="Andorra" />
                    <SelectItem value="AO" text="Angola" />
                    <SelectItem value="AG" text="Antigua and Barbuda" />
                    <SelectItem value="AR" text="Argentina" />
                    <SelectItem value="AM" text="Armenia" />
                    <SelectItem value="AU" text="Australia" />
                    <SelectItem value="AT" text="Austria" />
                    <SelectItem value="AZ" text="Azerbaijan" />
                    <SelectItem value="BS" text="Bahamas" />
                    <SelectItem value="BH" text="Bahrain" />
                    <SelectItem value="BD" text="Bangladesh" />
                    <SelectItem value="BB" text="Barbados" />
                    <SelectItem value="BY" text="Belarus" />
                    <SelectItem value="BE" text="Belgium" />
                    <SelectItem value="BZ" text="Belize" />
                    <SelectItem value="BJ" text="Benin" />
                    <SelectItem value="BT" text="Bhutan" />
                    <SelectItem value="BO" text="Bolivia" />
                    <SelectItem value="BA" text="Bosnia and Herzegovina" />
                    <SelectItem value="BW" text="Botswana" />
                    <SelectItem value="BR" text="Brazil" />
                    <SelectItem value="BN" text="Brunei" />
                    <SelectItem value="BG" text="Bulgaria" />
                    <SelectItem value="BF" text="Burkina Faso" />
                    <SelectItem value="BI" text="Burundi" />
                    <SelectItem value="KH" text="Cambodia" />
                    <SelectItem value="CM" text="Cameroon" />
                    <SelectItem value="CA" text="Canada" />
                    <SelectItem value="CV" text="Cape Verde" />
                    <SelectItem value="KY" text="Cayman Islands" />
                    <SelectItem value="CF" text="Central African Republic" />
                    <SelectItem value="TD" text="Chad" />
                    <SelectItem value="CL" text="Chile" />
                    <SelectItem value="CN" text="China" />
                    <SelectItem value="CO" text="Colombia" />
                    <SelectItem value="KM" text="Comoros" />
                    <SelectItem value="CG" text="Congo" />
                    <SelectItem value="CD" text="Congo, Democratic Republic" />
                    <SelectItem value="CR" text="Costa Rica" />
                    <SelectItem value="CI" text="Côte d'Ivoire" />
                    <SelectItem value="HR" text="Croatia" />
                    <SelectItem value="CU" text="Cuba" />
                    <SelectItem value="CY" text="Cyprus" />
                    <SelectItem value="CZ" text="Czech Republic" />
                    <SelectItem value="DK" text="Denmark" />
                    <SelectItem value="DJ" text="Djibouti" />
                    <SelectItem value="DM" text="Dominica" />
                    <SelectItem value="DO" text="Dominican Republic" />
                    <SelectItem value="EC" text="Ecuador" />
                    <SelectItem value="EG" text="Egypt" />
                    <SelectItem value="SV" text="El Salvador" />
                    <SelectItem value="GQ" text="Equatorial Guinea" />
                    <SelectItem value="ER" text="Eritrea" />
                    <SelectItem value="EE" text="Estonia" />
                    <SelectItem value="SZ" text="Eswatini" />
                    <SelectItem value="ET" text="Ethiopia" />
                    <SelectItem value="FJ" text="Fiji" />
                    <SelectItem value="FI" text="Finland" />
                    <SelectItem value="FR" text="France" />
                    <SelectItem value="GA" text="Gabon" />
                    <SelectItem value="GM" text="Gambia" />
                    <SelectItem value="GE" text="Georgia" />
                    <SelectItem value="DE" text="Germany" />
                    <SelectItem value="GH" text="Ghana" />
                    <SelectItem value="GR" text="Greece" />
                    <SelectItem value="GD" text="Grenada" />
                    <SelectItem value="GT" text="Guatemala" />
                    <SelectItem value="GN" text="Guinea" />
                    <SelectItem value="GW" text="Guinea-Bissau" />
                    <SelectItem value="GY" text="Guyana" />
                    <SelectItem value="HT" text="Haiti" />
                    <SelectItem value="HN" text="Honduras" />
                    <SelectItem value="HK" text="Hong Kong" />
                    <SelectItem value="HU" text="Hungary" />
                    <SelectItem value="IS" text="Iceland" />
                    <SelectItem value="IN" text="India" />
                    <SelectItem value="ID" text="Indonesia" />
                    <SelectItem value="IR" text="Iran" />
                    <SelectItem value="IQ" text="Iraq" />
                    <SelectItem value="IE" text="Ireland" />
                    <SelectItem value="IL" text="Israel" />
                    <SelectItem value="IT" text="Italy" />
                    <SelectItem value="JM" text="Jamaica" />
                    <SelectItem value="JP" text="Japan" />
                    <SelectItem value="JO" text="Jordan" />
                    <SelectItem value="KZ" text="Kazakhstan" />
                    <SelectItem value="KE" text="Kenya" />
                    <SelectItem value="KI" text="Kiribati" />
                    <SelectItem value="KR" text="South Korea" />
                    <SelectItem value="KW" text="Kuwait" />
                    <SelectItem value="KG" text="Kyrgyzstan" />
                    <SelectItem value="LA" text="Laos" />
                    <SelectItem value="LV" text="Latvia" />
                    <SelectItem value="LB" text="Lebanon" />
                    <SelectItem value="LS" text="Lesotho" />
                    <SelectItem value="LR" text="Liberia" />
                    <SelectItem value="LY" text="Libya" />
                    <SelectItem value="LI" text="Liechtenstein" />
                    <SelectItem value="LT" text="Lithuania" />
                    <SelectItem value="LU" text="Luxembourg" />
                    <SelectItem value="MG" text="Madagascar" />
                    <SelectItem value="MW" text="Malawi" />
                    <SelectItem value="MY" text="Malaysia" />
                    <SelectItem value="MV" text="Maldives" />
                    <SelectItem value="ML" text="Mali" />
                    <SelectItem value="MT" text="Malta" />
                    <SelectItem value="MH" text="Marshall Islands" />
                    <SelectItem value="MR" text="Mauritania" />
                    <SelectItem value="MU" text="Mauritius" />
                    <SelectItem value="MX" text="Mexico" />
                    <SelectItem value="FM" text="Micronesia" />
                    <SelectItem value="MD" text="Moldova" />
                    <SelectItem value="MC" text="Monaco" />
                    <SelectItem value="MN" text="Mongolia" />
                    <SelectItem value="ME" text="Montenegro" />
                    <SelectItem value="MA" text="Morocco" />
                    <SelectItem value="MZ" text="Mozambique" />
                    <SelectItem value="MM" text="Myanmar" />
                    <SelectItem value="NA" text="Namibia" />
                    <SelectItem value="NR" text="Nauru" />
                    <SelectItem value="NP" text="Nepal" />
                    <SelectItem value="NL" text="Netherlands" />
                    <SelectItem value="NZ" text="New Zealand" />
                    <SelectItem value="NI" text="Nicaragua" />
                    <SelectItem value="NE" text="Niger" />
                    <SelectItem value="NG" text="Nigeria" />
                    <SelectItem value="MK" text="North Macedonia" />
                    <SelectItem value="NO" text="Norway" />
                    <SelectItem value="OM" text="Oman" />
                    <SelectItem value="PK" text="Pakistan" />
                    <SelectItem value="PW" text="Palau" />
                    <SelectItem value="PA" text="Panama" />
                    <SelectItem value="PG" text="Papua New Guinea" />
                    <SelectItem value="PY" text="Paraguay" />
                    <SelectItem value="PE" text="Peru" />
                    <SelectItem value="PH" text="Philippines" />
                    <SelectItem value="PL" text="Poland" />
                    <SelectItem value="PT" text="Portugal" />
                    <SelectItem value="QA" text="Qatar" />
                    <SelectItem value="RO" text="Romania" />
                    <SelectItem value="RU" text="Russia" />
                    <SelectItem value="RW" text="Rwanda" />
                    <SelectItem value="KN" text="Saint Kitts and Nevis" />
                    <SelectItem value="LC" text="Saint Lucia" />
                    <SelectItem value="VC" text="Saint Vincent and the Grenadines" />
                    <SelectItem value="WS" text="Samoa" />
                    <SelectItem value="SM" text="San Marino" />
                    <SelectItem value="ST" text="São Tomé and Príncipe" />
                    <SelectItem value="SA" text="Saudi Arabia" />
                    <SelectItem value="SN" text="Senegal" />
                    <SelectItem value="RS" text="Serbia" />
                    <SelectItem value="SC" text="Seychelles" />
                    <SelectItem value="SL" text="Sierra Leone" />
                    <SelectItem value="SG" text="Singapore" />
                    <SelectItem value="SK" text="Slovakia" />
                    <SelectItem value="SI" text="Slovenia" />
                    <SelectItem value="SB" text="Solomon Islands" />
                    <SelectItem value="SO" text="Somalia" />
                    <SelectItem value="ZA" text="South Africa" />
                    <SelectItem value="SS" text="South Sudan" />
                    <SelectItem value="ES" text="Spain" />
                    <SelectItem value="LK" text="Sri Lanka" />
                    <SelectItem value="SD" text="Sudan" />
                    <SelectItem value="SR" text="Suriname" />
                    <SelectItem value="SE" text="Sweden" />
                    <SelectItem value="CH" text="Switzerland" />
                    <SelectItem value="SY" text="Syria" />
                    <SelectItem value="TW" text="Taiwan" />
                    <SelectItem value="TJ" text="Tajikistan" />
                    <SelectItem value="TZ" text="Tanzania" />
                    <SelectItem value="TH" text="Thailand" />
                    <SelectItem value="TL" text="Timor-Leste" />
                    <SelectItem value="TG" text="Togo" />
                    <SelectItem value="TO" text="Tonga" />
                    <SelectItem value="TT" text="Trinidad and Tobago" />
                    <SelectItem value="TN" text="Tunisia" />
                    <SelectItem value="TR" text="Turkey" />
                    <SelectItem value="TM" text="Turkmenistan" />
                    <SelectItem value="TV" text="Tuvalu" />
                    <SelectItem value="UG" text="Uganda" />
                    <SelectItem value="UA" text="Ukraine" />
                    <SelectItem value="AE" text="United Arab Emirates" />
                    <SelectItem value="GB" text="United Kingdom" />
                    <SelectItem value="US" text="United States" />
                    <SelectItem value="UY" text="Uruguay" />
                    <SelectItem value="UZ" text="Uzbekistan" />
                    <SelectItem value="VU" text="Vanuatu" />
                    <SelectItem value="VE" text="Venezuela" />
                    <SelectItem value="VN" text="Vietnam" />
                    <SelectItem value="YE" text="Yemen" />
                    <SelectItem value="ZM" text="Zambia" />
                    <SelectItem value="ZW" text="Zimbabwe" />
                  </Select>
                </div>

                {/* Action Buttons - Stack on mobile */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Button
                    kind="primary"
                    type="submit"
                    size="lg"
                    renderIcon={ArrowRight}
                    style={{ width: '100%' }}
                    disabled={isLoading}
                    data-testid="continue-to-payment"
                  >
                    {isLoading ? 'Redirecting to Payment...' : `Continue to Payment - $${total.toFixed(2)}`}
                  </Button>
                  <Button
                    kind="secondary"
                    size="lg"
                    renderIcon={ArrowLeft}
                    onClick={() => navigate('/cart')}
                    style={{ width: '100%' }}
                    data-testid="back-to-cart"
                  >
                    Back to Cart
                  </Button>
                </div>
              </form>
            </Tile>
          </Column>

          {/* Order Summary */}
          <Column lg={6} md={8} sm={4}>
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px', position: 'sticky', top: '64px' }}>
              <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>Order Summary</h3>
              
              {/* Cart Items */}
              <div style={{ borderBottom: `1px solid ${borderColor}`, paddingBottom: '12px', marginBottom: '12px' }}>
                {cartItems.map((item) => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.875rem' }}>
                    <span style={{ color: textSecondary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: '8px' }}>
                      {item.quantity}x {item.name}
                    </span>
                    <span style={{ color: textPrimary, fontWeight: 500 }}>${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div style={{ fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: textSecondary }}>Subtotal</span>
                  <span style={{ color: textPrimary }}>${subtotal.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: textSecondary }}>Shipping</span>
                  <span style={{ color: shipping === 0 ? '#24a148' : textPrimary }}>
                    {shipping === 0 ? 'Included' : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ color: textSecondary }}>GST (10%)</span>
                  <span style={{ color: textPrimary }}>${tax.toFixed(2)}</span>
                </div>
              </div>

              <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: textPrimary, fontWeight: 600 }}>Total</span>
                  <span style={{ color: '#0f62fe', fontSize: '1.25rem', fontWeight: 600 }}>${total.toFixed(2)}</span>
                </div>
              </div>
            </Tile>
          </Column>
        </Grid>
      </div>
    </div>
  );
};

export default ShippingPage;
